/**
 * Design System
 * 
 * Unified styling and DOM generation utilities for all views.
 * Colors matched to actual 3D scene: deep blue-purple background, cyan network lines.
 */

/**
 * Design tokens
 * 
 * Colors matched to 3D scene gradient (hue 236.69):
 * - Deep blue-purple background (matches scene depth)
 * - Cyan accents (#6cf) matching network lines
 * - White highlights for readability
 */
export const designTokens = {
  // Colors - Matched to actual 3D scene gradient (hue 236.69, cool blue-purple)
  // Scene uses: deep blue-purple background, cyan network lines (#6cf), white highlights
  cardBackground: 'rgba(30, 32, 40, 0.95)', // Neutral dark slate (matches scene depth)
  contentColor: '#ffffff', // White content (maintains readability)
  contentColorMuted: 'rgba(255, 255, 255, 0.7)', // Muted white (no purple tint)
  borderColor: 'rgba(255, 255, 255, 0.1)', // Subtle white border (neutral)
  borderColorActive: 'rgba(102, 204, 255, 0.2)', // Cyan border (matches network lines)
  
  // Accent colors - From actual scene
  accent: '#6cf', // Cyan primary (matches network lines in scene)
  accentAI: '#6cf', // Cyan for AI active (same as network, not purple)
  accentCortex: '#6cf', // Cyan for cortex (matches scene)
  
  // Volumetric lighting colors - Subtle, matches scene
  rimLight: 'rgba(102, 204, 255, 0.08)', // Subtle cyan rim light (matches network glow)
  rimLightActive: 'rgba(102, 204, 255, 0.15)', // Slightly brighter when active
  fillLight: 'rgba(255, 255, 255, 0.02)', // Very subtle fill light
  shadowColor: 'rgba(0, 0, 0, 0.3)', // Deeper shadow for depth
  
  // Atmospheric effects - Subtle cyan glow (matches network lines)
  activeGlow: 'rgba(102, 204, 255, 0.15)', // Subtle cyan glow for active cards
  
  // Spacing
  padding: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px'
  },
  
  // Unified spacing constants for common patterns
  spacing: {
    cellPadding: '4px',        // Calendar day cells, grid cells
    gridGap: '2px',            // Gap between grid items
    listGap: '8px',            // Gap between list items
    eventBarHeight: '4px',     // Height of event bars in calendar
    priorityIndicatorSize: '12px', // Size of priority indicator squares
    itemPadding: '8px'         // Padding for list items
  },
  
  // Typography
  fontSize: {
    xs: '11px',
    sm: '12px',
    md: '13px',
    lg: '14px',
    xl: '16px'
  },
  
  // Border radius
  radius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  }
};

/**
 * Generate a heading element
 */
export function createHeading(
  level: 1 | 2 | 3 | 4 | 5 | 6,
  text: string,
  options: {
    className?: string;
    style?: string;
    margin?: string;
  } = {}
): string {
  const tag = `h${level}`;
  const defaultStyle = `
    margin: ${options.margin || '0 0 12px 0'};
    font-size: ${level === 1 ? designTokens.fontSize.xl : level === 2 ? designTokens.fontSize.lg : designTokens.fontSize.md};
    font-weight: ${level <= 2 ? '600' : '500'};
    color: ${designTokens.contentColor};
    line-height: 1.3;
  `;
  
  return `<${tag} class="${options.className || ''}" style="${defaultStyle}${options.style || ''}">${escapeHtml(text)}</${tag}>`;
}

/**
 * Generate a button element
 */
export function createButton(
  text: string,
  options: {
    onClick?: string;
    className?: string;
    style?: string;
    variant?: 'primary' | 'secondary' | 'ghost';
    disabled?: boolean;
  } = {}
): string {
  const variantStyles = {
    primary: `
      background: rgba(255, 255, 255, 0.15);
      border: 1px solid ${designTokens.borderColor};
    `,
    secondary: `
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid ${designTokens.borderColor};
    `,
    ghost: `
      background: transparent;
      border: 1px solid transparent;
    `
  };
  
  const disabledStyle = options.disabled ? `
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  ` : '';
  
  const defaultStyle = `
    padding: ${designTokens.padding.sm} ${designTokens.padding.md};
    ${variantStyles[options.variant || 'primary']}
    border-radius: ${designTokens.radius.sm};
    color: ${designTokens.contentColor};
    font-size: ${designTokens.fontSize.sm};
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
    ${disabledStyle}
  `;
  
  const combinedOnClick = options.onClick;
  const onClickAttr = combinedOnClick ? `onclick="${combinedOnClick}"` : '';
  const disabledAttr = options.disabled ? 'disabled' : '';
  
  return `<button class="${options.className || ''}" ${onClickAttr} ${disabledAttr} style="${defaultStyle}${options.style || ''}">${escapeHtml(text)}</button>`;
}

/**
 * Generate a list item element
 */
export function createListItem(
  content: string,
  options: {
    className?: string;
    style?: string;
    variant?: 'default' | 'subtle' | 'highlight';
  } = {}
): string {
  const variantStyles = {
    default: `
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid ${designTokens.borderColor};
    `,
    subtle: `
      background: transparent;
      border: 1px solid transparent;
    `,
    highlight: `
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
    `
  };
  
  const defaultStyle = `
    padding: ${designTokens.padding.md};
    ${variantStyles[options.variant || 'default']}
    border-radius: ${designTokens.radius.sm};
    color: ${designTokens.contentColor};
    font-size: ${designTokens.fontSize.sm};
    line-height: 1.5;
  `;
  
  return `<li class="${options.className || ''}" style="${defaultStyle}${options.style || ''}">${content}</li>`;
}

/**
 * Generate a text input element
 */
export function createTextInput(
  options: {
    value?: string;
    placeholder?: string;
    className?: string;
    style?: string;
    type?: 'text' | 'textarea' | 'email' | 'password';
    rows?: number;
    name?: string;
    required?: boolean;
  } = {}
): string {
  const defaultStyle = `
    width: 100%;
    padding: ${designTokens.padding.md};
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid ${designTokens.borderColor};
    border-radius: ${designTokens.radius.sm};
    color: ${designTokens.contentColor};
    font-size: ${designTokens.fontSize.md};
    font-family: inherit;
    box-sizing: border-box;
  `;
  
  const valueAttr = options.value !== undefined ? `value="${escapeHtml(options.value)}"` : '';
  const placeholderAttr = options.placeholder ? `placeholder="${escapeHtml(options.placeholder)}"` : '';
  const nameAttr = options.name ? `name="${escapeHtml(options.name)}"` : '';
  const requiredAttr = options.required ? 'required' : '';
  const typeAttr = options.type && options.type !== 'textarea' ? `type="${options.type}"` : '';
  
  // Add sound handlers
  const focusHandler = ``;
  const inputHandler = ``;
  
  if (options.type === 'textarea') {
    return `<textarea class="${options.className || ''}" ${nameAttr} ${placeholderAttr} ${requiredAttr} ${focusHandler} ${inputHandler} rows="${options.rows || 4}" style="${defaultStyle}${options.style || ''}">${options.value || ''}</textarea>`;
  }
  
  return `<input ${typeAttr || 'type="text"'} class="${options.className || ''}" ${nameAttr} ${valueAttr} ${placeholderAttr} ${requiredAttr} ${focusHandler} ${inputHandler} style="${defaultStyle}${options.style || ''}" />`;
}

/**
 * Generate a checkbox element
 */
export function createCheckbox(
  label: string,
  options: {
    checked?: boolean;
    className?: string;
    style?: string;
    name?: string;
    value?: string;
  } = {}
): string {
  const checkedAttr = options.checked ? 'checked' : '';
  const nameAttr = options.name ? `name="${escapeHtml(options.name)}"` : '';
  const valueAttr = options.value ? `value="${escapeHtml(options.value)}"` : '';
  
  return `
    <label style="
      display: flex;
      align-items: center;
      gap: ${designTokens.padding.sm};
      font-size: ${designTokens.fontSize.sm};
      color: ${designTokens.contentColor};
      cursor: pointer;
    ">
      <input 
        type="checkbox" 
        class="${options.className || ''}" 
        ${nameAttr} 
        ${valueAttr} 
        ${checkedAttr}
        style="
          cursor: pointer;
          accent-color: ${designTokens.contentColor};
          ${options.style || ''}
        "
      />
      <span>${escapeHtml(label)}</span>
    </label>
  `;
}

/**
 * Generate a select dropdown element
 */
export function createSelect(
  options: Array<{ value: string; label: string }>,
  selectedValue?: string,
  selectOptions: {
    className?: string;
    style?: string;
    name?: string;
    placeholder?: string;
  } = {}
): string {
  const nameAttr = selectOptions.name ? `name="${escapeHtml(selectOptions.name)}"` : '';
  const placeholderAttr = selectOptions.placeholder ? `data-placeholder="${escapeHtml(selectOptions.placeholder)}"` : '';
  
  const defaultStyle = `
    width: 100%;
    padding: ${designTokens.padding.md};
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid ${designTokens.borderColor};
    border-radius: ${designTokens.radius.sm};
    color: ${designTokens.contentColor};
    font-size: ${designTokens.fontSize.md};
    font-family: inherit;
    box-sizing: border-box;
    cursor: pointer;
  `;
  
  const optionsHTML = options.map(opt => {
    const selected = opt.value === selectedValue ? 'selected' : '';
    return `<option value="${escapeHtml(opt.value)}" ${selected}>${escapeHtml(opt.label)}</option>`;
  }).join('');
  
  return `<select class="${selectOptions.className || ''}" ${nameAttr} ${placeholderAttr} style="${defaultStyle}${selectOptions.style || ''}">${optionsHTML}</select>`;
}

/**
 * Generate a form field with label and error message
 */
export function createFormField(
  label: string,
  inputHTML: string,
  options: {
    error?: string;
    className?: string;
    style?: string;
  } = {}
): string {
  return `
    <div class="form-field ${options.className || ''}" style="margin-bottom: ${designTokens.padding.md}; ${options.style || ''}">
      <label style="
        display: block;
        margin-bottom: ${designTokens.padding.xs};
        font-size: ${designTokens.fontSize.sm};
        font-weight: 500;
        color: ${designTokens.contentColor};
      ">${escapeHtml(label)}</label>
      ${inputHTML}
      ${options.error ? `
        <div style="
          margin-top: ${designTokens.padding.xs};
          font-size: ${designTokens.fontSize.xs};
          color: rgba(255, 0, 0, 0.8);
        ">${escapeHtml(options.error)}</div>
      ` : ''}
    </div>
  `;
}

/**
 * Generate a key-value list (for settings/JSON display)
 */
export function createKeyValueList(
  data: Record<string, unknown>,
  options: {
    className?: string;
    style?: string;
  } = {}
): string {
  const items = Object.entries(data).map(([key, value]) => {
    const valueStr = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
    return createListItem(`
      <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: ${designTokens.padding.md};">
        <div style="
          font-weight: 500;
          color: ${designTokens.contentColor};
          flex-shrink: 0;
          min-width: 120px;
        ">${escapeHtml(key)}:</div>
        <div style="
          color: ${designTokens.contentColorMuted};
          flex: 1;
          text-align: right;
          word-break: break-word;
          font-family: monospace;
          font-size: ${designTokens.fontSize.xs};
        ">${escapeHtml(valueStr)}</div>
      </div>
    `, { variant: 'subtle' });
  }).join('');
  
  return `<ul style="list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: ${designTokens.padding.sm}; ${options.style || ''}" class="${options.className || ''}">${items}</ul>`;
}

/**
 * Generate a container div with standard padding
 */
export function createContainer(
  content: string,
  options: {
    className?: string;
    style?: string;
    padding?: keyof typeof designTokens.padding;
    flexDirection?: 'row' | 'column';
  } = {}
): string {
  const defaultStyle = `
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: ${options.flexDirection || 'column'};
    padding: ${designTokens.padding[options.padding || 'lg']};
    box-sizing: border-box;
  `;
  
  return `<div class="${options.className || ''}" style="${defaultStyle}${options.style || ''}">${content}</div>`;
}

/**
 * Generate a scrollable container
 */
export function createScrollableContainer(
  content: string,
  options: {
    className?: string;
    style?: string;
    direction?: 'vertical' | 'horizontal' | 'both';
  } = {}
): string {
  const overflowMap = {
    vertical: 'overflow-y: auto; overflow-x: hidden;',
    horizontal: 'overflow-x: auto; overflow-y: hidden;',
    both: 'overflow: auto;'
  };
  
  const defaultStyle = `
    flex: 1;
    min-height: 0;
    ${overflowMap[options.direction || 'vertical']}
  `;
  
  return `<div class="${options.className || ''}" style="${defaultStyle}${options.style || ''}">${content}</div>`;
}

/**
 * Generate empty state message
 */
export function createEmptyState(
  message: string,
  options: {
    className?: string;
    style?: string;
  } = {}
): string {
  const defaultStyle = `
    padding: ${designTokens.padding.xl};
    text-align: center;
    color: ${designTokens.contentColorMuted};
    font-size: ${designTokens.fontSize.sm};
    font-style: italic;
  `;
  
  return `<div class="${options.className || ''}" style="${defaultStyle}${options.style || ''}">${escapeHtml(message)}</div>`;
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

